import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '@/shared/seo/seoRouting';

const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

interface HreflangEntry {
  href: string;
  hrefLang: string;
}

interface SeoHelmetProps {
  title?: string;
  description?: string;
  author?: string;
  canonical?: string;
  ogImage?: string;
  twitterImage?: string;
  ogType?: string;
  publishedTime?: string;
  modifiedTime?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown>[];
  hreflang?: HreflangEntry[];
}

export function SeoHelmet({
  title,
  description,
  author,
  canonical,
  ogImage,
  twitterImage,
  ogType = 'website',
  publishedTime,
  modifiedTime,
  noindex,
  jsonLd = [],
  hreflang = [],
}: SeoHelmetProps) {
  const image = ogImage || DEFAULT_OG_IMAGE;
  const twImage = twitterImage || image;
  return (
    <Helmet>
      {title ? <title>{title}</title> : null}
      {title ? <meta property="og:title" content={title} /> : null}
      {title ? <meta name="twitter:title" content={title} /> : null}
      {description ? <meta name="description" content={description} /> : null}
      {description ? <meta property="og:description" content={description} /> : null}
      {description ? <meta name="twitter:description" content={description} /> : null}
      {author ? <meta name="author" content={author} /> : null}
      {author && ogType === 'article' ? <meta property="article:author" content={author} /> : null}
      {publishedTime && ogType === 'article' ? <meta property="article:published_time" content={publishedTime} /> : null}
      {modifiedTime && ogType === 'article' ? <meta property="article:modified_time" content={modifiedTime} /> : null}
      {canonical ? <link rel="canonical" href={canonical} /> : null}
      {canonical ? <meta property="og:url" content={canonical} /> : null}
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={twImage} />
      <meta property="og:type" content={ogType} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />

      {hreflang.map((entry) => (
        <link key={entry.hrefLang} rel="alternate" hrefLang={entry.hrefLang} href={entry.href} />
      ))}

      {jsonLd.map((payload, idx) => (
        <script key={`jsonld-${String(idx)}`} type="application/ld+json">
          {JSON.stringify(payload)}
        </script>
      ))}
    </Helmet>
  );
}
